// End-to-end encryption primitives (M28, docs/end-to-end-encryption.md).
// WebCrypto only. Device keys are ECDH P-256; chat (and family) keys are
// AES-256-GCM; a chat key is wrapped for each device with a key derived from
// ECDH + HKDF. Everything on the wire is base64.

const subtle = () => globalThis.crypto.subtle;
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const IV_BYTES = 12;

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < view.length; i += 0x8000) {
    binary += String.fromCharCode(...view.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function randomIv(): Uint8Array<ArrayBuffer> {
  return globalThis.crypto.getRandomValues(
    new Uint8Array(new ArrayBuffer(IV_BYTES))
  );
}

/** iv || ciphertext, as one buffer. */
function join(iv: Uint8Array, data: ArrayBuffer): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(iv.length + data.byteLength));
  out.set(iv, 0);
  out.set(new Uint8Array(data), iv.length);
  return out;
}

// ---- Device keys (D55) ------------------------------------------------------

/** A new device key pair. The private key can't be exported, only used. */
export function generateDeviceKeyPair(): Promise<CryptoKeyPair> {
  return subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, [
    'deriveBits',
  ]) as Promise<CryptoKeyPair>;
}

export async function exportPublicKey(key: CryptoKey): Promise<string> {
  return toBase64(await subtle().exportKey('spki', key));
}

export function importPublicKey(spki: string): Promise<CryptoKey> {
  return subtle().importKey(
    'spki',
    fromBase64(spki),
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    []
  );
}

/**
 * The key that wraps a chat or family key between two devices. Both sides
 * derive the same key from their own private key and the other's public key.
 */
async function wrappingKey(
  myPrivate: CryptoKey,
  theirPublic: CryptoKey,
  scopeId: string,
  epoch: number,
  kind: 'chat' | 'family'
): Promise<CryptoKey> {
  const shared = await subtle().deriveBits(
    { name: 'ECDH', public: theirPublic },
    myPrivate,
    256
  );
  const hkdf = await subtle().importKey('raw', shared, 'HKDF', false, [
    'deriveKey',
  ]);
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: encoder.encode(scopeId),
      info: encoder.encode(`twinden ${kind} key v1 ${epoch}`),
    },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---- Chat and family keys (D56) --------------------------------------------

export function generateContentKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export interface WrapScope {
  scopeId: string;
  epoch: number;
  kind: 'chat' | 'family';
}

/** A content key wrapped for one device: base64(iv || AES-GCM(raw key)). */
export async function wrapContentKey(
  contentKey: CryptoKey,
  myPrivate: CryptoKey,
  theirPublic: string,
  scope: WrapScope
): Promise<string> {
  const key = await wrappingKey(
    myPrivate,
    await importPublicKey(theirPublic),
    scope.scopeId,
    scope.epoch,
    scope.kind
  );
  const raw = await subtle().exportKey('raw', contentKey);
  const iv = randomIv();
  return toBase64(
    join(iv, await subtle().encrypt({ name: 'AES-GCM', iv }, key, raw))
  );
}

export async function unwrapContentKey(
  wrapped: string,
  myPrivate: CryptoKey,
  wrapperPublic: string,
  scope: WrapScope
): Promise<CryptoKey> {
  const key = await wrappingKey(
    myPrivate,
    await importPublicKey(wrapperPublic),
    scope.scopeId,
    scope.epoch,
    scope.kind
  );
  const bytes = fromBase64(wrapped);
  const raw = await subtle().decrypt(
    { name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES) },
    key,
    bytes.slice(IV_BYTES)
  );
  // Extractable so this device can pass it on to others (D57).
  return subtle().importKey('raw', raw, 'AES-GCM', true, [
    'encrypt',
    'decrypt',
  ]);
}

// ---- Envelopes (D59) -------------------------------------------------------

/** What binds a v1 envelope to its place: the chat and the message's clientId. */
export function messageAad(conversationId: string, clientId: string): string {
  return `twinden msg v1|${conversationId}|${clientId}`;
}

/** v1's text associated data, or v2's bytes (envelopeV2.ts, D324). */
export type Aad = string | Uint8Array<ArrayBuffer>;
const aadBytes = (aad: Aad) =>
  typeof aad === 'string' ? encoder.encode(aad) : aad;

export async function sealEnvelope(
  key: CryptoKey,
  value: unknown,
  aad: Aad,
  /** Only for the shared vector (M76): a fixed IV. Never in the app. */
  fixedIv?: Uint8Array<ArrayBuffer>
): Promise<string> {
  const iv = fixedIv ?? randomIv();
  const data = await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: aadBytes(aad) },
    key,
    encoder.encode(JSON.stringify(value))
  );
  return toBase64(join(iv, data));
}

export async function openEnvelope<T = unknown>(
  key: CryptoKey,
  sealed: string,
  aad: Aad
): Promise<T> {
  const bytes = fromBase64(sealed);
  const data = await subtle().decrypt(
    {
      name: 'AES-GCM',
      iv: bytes.slice(0, IV_BYTES),
      additionalData: aadBytes(aad),
    },
    key,
    bytes.slice(IV_BYTES)
  );
  return JSON.parse(decoder.decode(data)) as T;
}

// ---- Files (D59) -----------------------------------------------------------

/** A Blob's bytes (FileReader where Blob.arrayBuffer is missing, e.g. jsdom). */
export function readBlob(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export interface FileSecret {
  key: string;
  iv: string;
}

/** Encrypts a file with its own fresh key; the key travels in the envelope. */
export async function encryptFile(
  file: Blob
): Promise<{ blob: Blob; secret: FileSecret }> {
  const key = await subtle().generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt']
  );
  const iv = randomIv();
  const data = await subtle().encrypt(
    { name: 'AES-GCM', iv },
    key,
    await readBlob(file)
  );
  return {
    blob: new Blob([data], { type: 'application/octet-stream' }),
    secret: {
      key: toBase64(await subtle().exportKey('raw', key)),
      iv: toBase64(iv),
    },
  };
}

export async function decryptFile(
  data: ArrayBuffer,
  secret: FileSecret,
  mimeType: string
): Promise<Blob> {
  const key = await subtle().importKey(
    'raw',
    fromBase64(secret.key),
    'AES-GCM',
    false,
    ['decrypt']
  );
  const plain = await subtle().decrypt(
    { name: 'AES-GCM', iv: fromBase64(secret.iv) },
    key,
    data
  );
  return new Blob([plain], { type: mimeType || 'application/octet-stream' });
}

// ---- Security codes (D63) --------------------------------------------------

/**
 * A person's security code: 30 digits in groups of five, from a hash of their
 * device public keys in a fixed order. Same devices, same code.
 */
export async function securityCode(publicKeys: string[]): Promise<string> {
  const digest = new Uint8Array(
    await subtle().digest(
      'SHA-256',
      encoder.encode([...publicKeys].sort().join('\n'))
    )
  );
  const groups: string[] = [];
  for (let i = 0; i < 6; i += 1) {
    const chunk = digest.slice(i * 5, i * 5 + 5);
    const value = chunk.reduce((acc, byte) => acc * 256 + byte, 0) % 100000;
    groups.push(String(value).padStart(5, '0'));
  }
  return groups.join(' ');
}
